import React, { useEffect, useRef, useMemo } from 'react';
import { defineAdSlot, displayAdSlot, destroyAdSlot, startAdRefresh } from '@/utils/gpt';
import type { GoogleAdsPlacement } from '@/hooks/useTenant';

export interface AdSlotProps {
  /**
   * Unique ID for the ad container div
   */
  divId: string;

  /**
   * Google Ads placement configuration
   */
  adPlacement: GoogleAdsPlacement;

  /**
   * Optional CSS class for the container
   */
  className?: string;

  /**
   * Placement name for logging/debugging
   */
  placement?: string;

  /**
   * Optional: Enable automatic ad refresh
   * DraftKings spec recommends 30-60 second intervals
   */
  enableRefresh?: boolean;

  /**
   * Optional: Refresh interval in seconds (default: 45)
   */
  refreshIntervalSeconds?: number;

  /**
   * Optional: Maximum number of refreshes (0 = unlimited)
   */
  maxRefreshes?: number;
}

/**
 * AdSlot component for rendering Google Ad Manager ads
 * Based on DraftKings GPT integration requirements
 * Supports responsive size mappings and automatic ad refresh
 */
export const AdSlot: React.FC<AdSlotProps> = ({
  divId,
  adPlacement,
  className = '',
  placement = 'unknown',
  enableRefresh = false,
  refreshIntervalSeconds = 45,
  maxRefreshes = 0,
}) => {
  // Extract values from adPlacement
  const adUnitPath = useMemo(() => adPlacement.adUnitPath || '', [adPlacement.adUnitPath]);
  const sizes = useMemo(() => adPlacement.sizes || [], [adPlacement.sizes]);
  const sizeMappings = useMemo(() => adPlacement.sizeMappings, [adPlacement.sizeMappings]);
  const targeting = useMemo(() => adPlacement.targeting || undefined, [adPlacement.targeting]);
  const adRef = useRef<HTMLDivElement>(null);
  const isDefinedRef = useRef(false);
  const stopRefreshRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Define the ad slot only once
    if (!isDefinedRef.current) {
      defineAdSlot({
        adUnitPath,
        sizes,
        divId,
        targeting,
        sizeMappings, // Pass responsive size mappings
      });

      isDefinedRef.current = true;
    }

    // Display the ad
    displayAdSlot(divId);

    // Start automatic refresh if enabled
    if (enableRefresh) {
      stopRefreshRef.current = startAdRefresh({
        divIds: [divId],
        intervalSeconds: refreshIntervalSeconds,
        maxRefreshes,
      });
    }

    // Cleanup: destroy slot and stop refresh when component unmounts
    return () => {
      // Stop ad refresh if it was started
      if (stopRefreshRef.current) {
        stopRefreshRef.current();
        stopRefreshRef.current = null;
      }

      if (isDefinedRef.current) {
        destroyAdSlot(divId);
        isDefinedRef.current = false;
      }
    };
  }, [
    divId,
    adUnitPath,
    sizes,
    sizeMappings,
    targeting,
    placement,
    enableRefresh,
    refreshIntervalSeconds,
    maxRefreshes,
  ]);

  return (
    <div
      data-test-id={`ad-slot-${placement}`}
      className={`ad-slot-container relative w-full flex items-center justify-center ${className}`}
      style={{
        minHeight: `${Math.max(...sizes.map(s => s[1]))}px`,
      }}
    >
      <div
        id={divId}
        ref={adRef}
        className="ad-slot mx-auto"
        style={{
          maxWidth: `${Math.max(...sizes.map(s => s[0]))}px`,
        }}
      />
    </div>
  );
};

export default AdSlot;
